export interface SourceShortcutsList {
  [source: string]: string[]
}

export const sourceShortcuts: SourceShortcutsList = {
  default: ['state', 'defaultSlice'],
  globalPromotions: ['state', 'globalPromotionsSlice'],
  config: ['state', 'defaultSlice', 'config'],
  params: ['state', 'defaultSlice', 'params'],
  texts: ['state', 'defaultSlice', 'config', 'texts'],
  templates: ['state', 'defaultSlice', 'config', 'templates'],
  globals: ['state', 'defaultSlice', 'config', 'globals'],
  catalog: ['state', 'catalogSlice'],
  cart: ['state', 'cartSlice'],
  summary: ['state', 'summarySlice'],
  orders: ['state', 'ordersSlice'],
  filters: ['state', 'filtersSlice'],
  banners: ['state', 'bannersSlice'],
  categoriesNav: ['state', 'categoriesNavSlice'],
  /**
   * The storefront's **enforcement** rule map, off the session.
   *
   * ⚠️ Not the same subsystem as `checkout` below, despite the names. This one
   * is `{ quantity, message, isActive }` entries — two of them also carry
   * `sku` — keyed camelCase, and `checkoutSessionRule` evaluates them at order
   * creation. Putting an unhandled key into it disables that storefront's
   * enforcement outright (COM-736).
   *
   * Read the keys off `ICheckoutRules` in `graphQL/queries/getSession/types.ts`
   * rather than from an example here. It declares six —
   * `cartConditionPendingMinAmount` through `cartWarningCheckReturnables` — and
   * they are **not** the same set as the camelCase members of `CheckoutRules`
   * in `interfaces/config.ts`, which has nine.
   * `cartConditionCheckLinkedProducts` is in the config enum and not on this
   * type, so a name being a legal checkout-rule name does not make it a key
   * this shortcut can reach.
   *
   * ⚠️ `blocks` and `gates` are **not** fields here. They are the
   * per-storefront shape the M2 scoping doc designs for the *storefront*
   * backend, and `blocks` also names a field on `IRule` — the evaluated
   * *output* of `checkoutSessionRule`, a different structure from this input
   * map. An earlier revision of this docstring listed them as members of it.
   */
  checkoutRules: [
    'state',
    'defaultSlice',
    'sessionData',
    'configuration',
    'checkoutRules',
  ],
  /**
   * The M2 pickers' **selection** slice.
   *
   * ⚠️ Not the same subsystem as `checkoutRules` above. This one holds what a
   * shopper is choosing on the delivery-date and payment-method screens, keyed
   * SCREAMING_SNAKE by rule type — `DELIVERY_DATES`, `PAYMENT_METHODS`.
   * Confusing the two has produced a wrong answer four times across M1-C and
   * M2 scoping, which is why both entries carry a docstring.
   *
   * The half config reaches for is `checkout.ui.<RULE_TYPE>`: derived booleans
   * shaped for the props they gate, so a storefront can bind a `Text`'s
   * `hidden` or a `Button`'s `disabled` without the config language needing a
   * conditional. `selections` and `available` are readable too, but a
   * predicate over them belongs in the slice rather than in a binding — see
   * `ICheckoutRuleUiFlags`.
   */
  checkout: ['state', 'checkoutSlice'],
}

export const constantSources = ['config', 'texts', 'templates', 'globals']
